import { type ListingProduct, ListingProductCode } from 'src/generated/graphql';
import type { ClientListingForListingsTab } from 'src/types/graphql-derived';
import type { ProductBundle } from 'src/types/productBundle';

import { isNonNullable } from '.';

const findContract = (
  selectedProducts: ListingProduct[],
): ProductBundle['contract'] | undefined => {
  const adPack = selectedProducts.find((product) => {
    // Most ad pack products are prefixed with "SixAdPack". Whitelist those that aren't
    const nonPrefixedAdPackProducts =
      product.productCode === ListingProductCode.BoostedAd ||
      product.productCode === ListingProductCode.EmailBoost ||
      product.productCode === ListingProductCode.EmailBoostAndSearchBoost;
    return (
      product.productCode.includes(ListingProductCode.SixAdPack) ||
      nonPrefixedAdPackProducts
    );
  });
  const lus = selectedProducts.find((product) => {
    // Most LUS products are prefixed with "Lus". Whitelist those that aren't
    const nonPrefixedLusProducts = product.productCode.includes(
      ListingProductCode.ListUntilSold,
    );
    return product.productCode.includes('Lus') || nonPrefixedLusProducts;
  });
  const payPerEnquiry = selectedProducts.find(
    (product) => product.productCode === ListingProductCode.PayPerEnquiry,
  );
  const accelerate = selectedProducts.find(
    (product) => product.productCode === ListingProductCode.Accelerate,
  );

  if (adPack) return ListingProductCode.SixAdPack;
  else if (lus) return ListingProductCode.ListUntilSold;
  else if (payPerEnquiry) return ListingProductCode.PayPerEnquiry;
  else if (accelerate) return ListingProductCode.Accelerate;
};

const findBoostProduct = (
  listingProducts: ListingProduct[],
  contract: ProductBundle['contract'],
  boosting: 'Search' | 'Email',
) => {
  switch (contract.concat(boosting)) {
    case `${ListingProductCode.SixAdPack}Search`:
      return findListingProductByProductCode(
        listingProducts,
        ListingProductCode.BoostedAd,
      );
    case `${ListingProductCode.SixAdPack}Email`:
      return findListingProductByProductCode(
        listingProducts,
        ListingProductCode.EmailBoost,
      );
    case `${ListingProductCode.ListUntilSold}Search`:
      return findListingProductByProductCode(
        listingProducts,
        ListingProductCode.BoostedAdLus,
      );
    case `${ListingProductCode.ListUntilSold}Email`:
      return findListingProductByProductCode(
        listingProducts,
        ListingProductCode.EmailBoostLus,
      );
    default:
      return undefined;
  }
};

export const findListingProduct = <
  T extends {
    productToken: string;
  },
>(
  listingProducts?: T[],
  productToken?: string | Array<string | null> | null,
): T | undefined =>
  listingProducts?.find(
    (product) =>
      product.productToken.toLowerCase() ===
      productToken?.toString().toLowerCase(),
  );

export const findListingProducts = <
  T extends {
    productToken: string;
  },
>(
  listingProducts?: T[],
  productTokens?: string | Array<string | null> | null,
): T[] | undefined => {
  if (!listingProducts) {
    return undefined;
  }

  if (typeof productTokens === 'string') {
    const product = findListingProduct(listingProducts, productTokens);
    return product ? [product] : undefined;
  }

  if (Array.isArray(productTokens) && productTokens.length > 0) {
    const products = productTokens
      .map((productToken) => findListingProduct(listingProducts, productToken))
      .filter(isNonNullable);
    return products.length > 0 ? products : undefined;
  }

  return undefined;
};

export const findListingProductByProductCode = <
  T extends {
    productCode: ListingProductCode;
  },
>(
  listingProducts: T[],
  productCode: ListingProductCode,
): T | undefined =>
  // Change to use isListingProduct(productCode)
  listingProducts.find((x) => x.productCode === productCode);

export const getIsListingLus = <
  TListingProduct extends {
    productCode: ListingProductCode;
    legacyProductCode: string;
  },
  TListing extends {
    legacyProductCode: string | null;
  },
>(
  listingProducts: TListingProduct[],
  listing: TListing,
) => {
  const selectedProduct = listingProducts.find(
    (x) => x.legacyProductCode === listing.legacyProductCode,
  );

  const isAdPack =
    selectedProduct?.productCode === ListingProductCode.SixAdPack;
  return !isAdPack;
};

export const getSearchBoostProduct = <
  T extends {
    productCode: ListingProductCode;
    legacyProductCode: string;
  },
>(
  listingProducts: T[],
  listing: ClientListingForListingsTab,
) => {
  const selectedProduct = listingProducts.find(
    (x) => x.legacyProductCode === listing.legacyProductCode,
  );

  if (!selectedProduct) return;

  const searchBoostProduct =
    selectedProduct.productCode === ListingProductCode.SixAdPack
      ? ListingProductCode.BoostedAd
      : ListingProductCode.BoostedAdLus;
  return findListingProductByProductCode(listingProducts, searchBoostProduct);
};

export const getEmailBoostProduct = <
  T extends {
    productCode: ListingProductCode;
    legacyProductCode: string;
  },
>(
  listingProducts: T[],
  listing: ClientListingForListingsTab,
) => {
  const selectedProduct = listingProducts.find(
    (x) => x.legacyProductCode === listing.legacyProductCode,
  );

  if (!selectedProduct) return;

  const emailBoostProduct =
    selectedProduct.productCode === ListingProductCode.SixAdPack
      ? ListingProductCode.EmailBoost
      : ListingProductCode.EmailBoostLus;

  return findListingProductByProductCode(listingProducts, emailBoostProduct);
};

/**
 * Base listing contracts are the fundamental contracts that support posting a listing
 */
export const isBaseListingProduct = (productCode: ListingProductCode) =>
  [
    ListingProductCode.SixAdPack,
    ListingProductCode.SixAdPackAndSearchBoost,
    ListingProductCode.ListUntilSold,
    ListingProductCode.ListUntilSoldAndSearchBoostLus,
  ].includes(productCode);

export const isPayPerEnquiry = (productCode: string) =>
  productCode === ListingProductCode.PayPerEnquiry;
export const isCustomEmailBoost = (productCode: string) =>
  productCode === ListingProductCode.EmailBoostCustom;

export const isFreemium = <
  T extends {
    productCode: ListingProductCode;
    legacyProductCode: string;
  },
>(
  listingProducts: T[],
  listing: ClientListingForListingsTab,
) =>
  listing.legacyProductCode ===
  listingProducts.find((x) => x.productCode === ListingProductCode.Freemium)
    ?.legacyProductCode;

export const isSearchBoostOnly = (productCode: string) =>
  (!productCode.includes(ListingProductCode.EmailBoostAndSearchBoost) &&
    productCode.includes('SearchBoost')) ||
  productCode.includes(ListingProductCode.BoostedAd);

export const isProductWithBoost = (productCode: string) =>
  productCode !== ListingProductCode.ListUntilSold &&
  productCode !== ListingProductCode.SixAdPack;

export const isUpsellProduct = (productCode: string) =>
  productCode === ListingProductCode.SixAdPackAndSearchBoost ||
  productCode === ListingProductCode.ListUntilSoldAndSearchBoostLus;

const areInvalidProducts = (productCodes: string[]): boolean =>
  productCodes.some((c) => c === ListingProductCode.EmailBoostCustom) ||
  ((productCodes.some((c) => c.includes(ListingProductCode.SixAdPack)) ||
    productCodes.some((c) => c === ListingProductCode.BoostedAd)) &&
    (productCodes.some((c) => c.includes(ListingProductCode.ListUntilSold)) ||
      productCodes.some((c) => c.includes('Lus'))));

export const initProductBundle = (
  listingProducts: ListingProduct[],
  products: ListingProduct[],
  hasListingSearchBoosted?: boolean,
): { type: 'success'; value: ProductBundle } | { type: 'error' } => {
  const productCodes = products.map((product) => product.productCode);

  if (areInvalidProducts(productCodes)) {
    return { type: 'error' };
  }

  const contract = findContract(products);

  if (!contract) {
    return { type: 'error' };
  }

  const isLus = contract === ListingProductCode.ListUntilSold;

  const isSearchBoosted =
    hasListingSearchBoosted ||
    productCodes.some(
      (c) =>
        c.includes(ListingProductCode.BoostedAd) || c.includes('SearchBoost'),
    );
  const isEmailBoosted =
    productCodes.some((c) => c.includes(ListingProductCode.EmailBoost)) &&
    !productCodes.includes(ListingProductCode.EmailBoostCustom);

  const searchAndEmailBoost = isLus
    ? ListingProductCode.EmailBoostAndSearchBoostLus
    : ListingProductCode.EmailBoostAndSearchBoost;

  if (!hasListingSearchBoosted && isSearchBoosted && isEmailBoosted) {
    const product = findListingProductByProductCode(
      listingProducts,
      searchAndEmailBoost,
    );

    if (!product) {
      return { type: 'error' };
    }

    return {
      type: 'success',
      value: {
        contract,
        info: product,
        boostComponents: {
          search: findBoostProduct(listingProducts, contract, 'Search'),
          email: findBoostProduct(listingProducts, contract, 'Email'),
        },
        isBoostBundle: true,
      },
    };
  } else if (!hasListingSearchBoosted && isSearchBoosted) {
    const includeBaseContract = productCodes.some(
      (c) =>
        c.includes(ListingProductCode.SixAdPack) ||
        c.includes(ListingProductCode.ListUntilSold),
    );
    let productCode;
    if (includeBaseContract) {
      productCode = isLus
        ? ListingProductCode.ListUntilSoldAndSearchBoostLus
        : ListingProductCode.SixAdPackAndSearchBoost;
    } else {
      productCode = isLus
        ? ListingProductCode.BoostedAdLus
        : ListingProductCode.BoostedAd;
    }

    const bundleProduct = findListingProductByProductCode(
      listingProducts,
      productCode,
    );

    if (!bundleProduct) {
      return { type: 'error' };
    }

    return {
      type: 'success',
      value: {
        contract,
        info: bundleProduct,
        availableEmailBoost: findListingProductByProductCode(
          listingProducts,
          searchAndEmailBoost,
        ),
        boostComponents: {
          email: findBoostProduct(listingProducts, contract, 'Email'),
          search: findBoostProduct(listingProducts, contract, 'Search'),
        },
      },
    };
  } else if (isEmailBoosted) {
    const productCode = isLus
      ? ListingProductCode.EmailBoostLus
      : ListingProductCode.EmailBoost;

    const bundleProduct = findListingProductByProductCode(
      listingProducts,
      productCode,
    );

    if (!bundleProduct) {
      return { type: 'error' };
    }

    const productBundle: ProductBundle = {
      contract,
      info: bundleProduct,
    };
    productBundle.boostComponents = {
      email: findBoostProduct(listingProducts, contract, 'Email'),
    };

    if (!hasListingSearchBoosted) {
      productBundle.availableSearchBoost = findListingProductByProductCode(
        listingProducts,
        searchAndEmailBoost,
      );
      productBundle.boostComponents.search = findBoostProduct(
        listingProducts,
        contract,
        'Search',
      );
    }

    return {
      type: 'success',
      value: productBundle,
    };
  }
  // eslint-disable-next-line no-else-return
  else {
    const firstProduct = products[0];
    const productBundle: ProductBundle = {
      contract,
      info: firstProduct,
    };

    if (isPayPerEnquiry(firstProduct.productCode)) {
      return { type: 'success', value: productBundle };
    }

    const emailBoosted = isLus
      ? ListingProductCode.EmailBoostLus
      : ListingProductCode.EmailBoost;
    productBundle.availableEmailBoost = findListingProductByProductCode(
      listingProducts,
      emailBoosted,
    );
    productBundle.boostComponents = {
      email: findBoostProduct(listingProducts, contract, 'Email'),
    };

    if (!hasListingSearchBoosted) {
      const searchBoosted = isLus
        ? ListingProductCode.ListUntilSoldAndSearchBoostLus
        : ListingProductCode.SixAdPackAndSearchBoost;
      productBundle.availableSearchBoost = findListingProductByProductCode(
        listingProducts,
        searchBoosted,
      );
      productBundle.boostComponents.search = findBoostProduct(
        listingProducts,
        contract,
        'Search',
      );
    }

    return { type: 'success', value: productBundle };
  }
};
